<template>
  <!-- Hero: um banner por vez, largura total e mais alto -->
  <template v-if="isHero">
    <div v-if="filteredBanners.length" class="sf-banners sf-banners--hero px-4 sm:px-0 sm:pb-8">
      <swiper
        :slides-per-view="1"
        :space-between="0"
        :pagination="{ clickable: true }"
        :autoplay="filteredBanners.length > 1 ? { delay: 5000, disableOnInteraction: false } : false"
        :loop="filteredBanners.length > 1"
        :modules="modules"
        class="pb-10"
      >
        <swiper-slide v-for="banner in filteredBanners" :key="banner.id">
          <div class="sf-banners__hero-wrapper w-full overflow-hidden" :class="{ 'cursor-pointer': banner.actionType }" @click="handleBannerClick(banner)">
            <img class="w-full h-full object-cover" :src="getBannerImage(banner)" alt="">
          </div>
        </swiper-slide>
      </swiper>
    </div>
    <div v-if="!restaurant.id" class="sf-banners sf-banners--hero animate-pulse px-4 sm:px-0 sm:pb-8">
      <div class="sf-banners__hero-wrapper sf-skeleton w-full mb-10"></div>
    </div>
  </template>

  <!-- Carrossel (padrão) -->
  <template v-else>
    <div class="home__banners sf-banners sf-banners--carousel pl-4 sm:pl-0 sm:pb-8" v-if="filteredBanners.length">
      <swiper
        :slides-per-view="3"
        :space-between="50"
        :pagination="true"
        :modules="modules"
        :breakpoints="carouselBreakpoints"
        :free-mode="true"
        class="pb-10"
      >
        <swiper-slide class="pr-4 sm:p-0" v-for="banner in filteredBanners" :key="banner.id">
          <div class="home__banners__wrapper w-full rounded-xl cursor-pointer" @click="handleBannerClick(banner)">
            <img style="perspective: 1px;" class="w-full img-skeleton rounded-2xl object-fill" :src="getBannerImage(banner)" alt="">
          </div>
        </swiper-slide>
      </swiper>
    </div>

    <div v-if="!restaurant.id" class="home__banners sf-banners sf-banners--carousel animate-pulse pl-4 sm:pl-0 sm:pb-8">
      <swiper
        :slides-per-view="3"
        :space-between="50"
        :pagination="true"
        :modules="modules"
        :breakpoints="carouselBreakpoints"
        :free-mode="true"
        class="pb-10"
      >
        <swiper-slide class="pr-4 sm:p-0" v-for="i in [1,2,3]" :key="i">
          <div class="sf-skeleton img-skeleton rounded-2xl"></div>
        </swiper-slide>
      </swiper>
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { Pagination, FreeMode, Autoplay } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import 'swiper/css'
import 'swiper/css/pagination'
import axiosClient from '@/api/axios'
import { restaurant, categories, apiBaseUrl, appliedCoupon } from '@/global/state/GlobalState'
import { BannersSection } from '@/global/state/StorefrontState'

/* global defineProps, defineEmits */
const props = defineProps<{ section: BannersSection }>()
const emit = defineEmits(['open-product'])

const modules = [Pagination, FreeMode, Autoplay]
const router = useRouter()
const toast = useToast()

const isHero = computed(() => props.section.settings?.style === 'hero')

const carouselBreakpoints = { 0: { slidesPerView: 1.13, spaceBetween: 0 }, 430: { slidesPerView: 1.3, spaceBetween: 10 }, 500: { slidesPerView: 1.3, spaceBetween: 20 }, 680: { slidesPerView: 1.7, spaceBetween: 20 }, 830: { slidesPerView: 2, spaceBetween: 20 }, 1050: { slidesPerView: 2.5, spaceBetween: 20 }, 1200: { slidesPerView: 3, spaceBetween: 20 } }

const isMobile = ref(window.matchMedia('(max-width: 768px)').matches)
const updateIsMobile = () => {
  isMobile.value = window.matchMedia('(max-width: 768px)').matches
}

const filteredBanners = computed<any[]>(() => {
  if (!restaurant.value.banners?.length) return []
  return restaurant.value.banners.filter((banner: any) => {
    if (!banner.deviceType || banner.deviceType === 'all') return true
    return isMobile.value ? banner.deviceType === 'mobile' : banner.deviceType === 'desktop'
  })
})

const getBannerImage = (banner: any) => {
  if (banner.source?.startsWith('http')) return banner.source
  return `${apiBaseUrl}/storage/${banner.source}`
}

const handleBannerClick = async (banner: any) => {
  if (!banner.actionType || !banner.actionValue) return
  if (banner.actionType === 'category') {
    router.push({ name: 'categories.show', params: { id: banner.actionValue } })
  } else if (banner.actionType === 'product') {
    const product = categories.value?.flatMap((c: any) => c.products || []).find((p: any) => p.id == banner.actionValue)
    if (product) emit('open-product', product)
  } else if (banner.actionType === 'coupon') {
    try {
      const response = await axiosClient.get(`customer/coupons/${banner.actionValue}`)
      appliedCoupon.value = response.data
      toast.success(`Cupom ${response.data.code} aplicado!`)
    } catch (error) {
      console.error('Erro ao aplicar cupom do banner:', error)
      toast.error('Cupom inválido ou expirado')
    }
  }
}

onMounted(() => window.addEventListener('resize', updateIsMobile))
onUnmounted(() => window.removeEventListener('resize', updateIsMobile))
</script>

<style lang="scss">
.home__banners {
  img, .img-skeleton {
    height: 13.4rem;
    width: 26rem;
    max-width: 100%;
  }

  @media screen and (max-width: 600px) {
    img, .img-skeleton {
      min-height: 150px !important;
      height: 170px !important;
      margin-bottom: 0 !important;
      width: 400px !important;
    }

    &__wrapper {
      height: 170px
    }
  }

  @media screen and (max-width: 520px) {
    img, .img-skeleton {
      height: 150px !important;
    }

    &__wrapper {
      height: 150px
    }
  }
}

/* Vitrine: carrossel com banners mais altos */
html.sf-template-showcase .sf-banners--carousel {
  img, .img-skeleton {
    height: 17rem;
  }
}

.sf-banners--hero {
  .sf-banners__hero-wrapper {
    border-radius: var(--sf-radius);
    height: 24rem;

    @media screen and (max-width: 1024px) {
      height: 18rem;
    }
    @media screen and (max-width: 600px) {
      height: 12rem;
    }
  }

  img {
    width: 100% !important;
    max-width: 100%;
    height: 100% !important;
    min-height: 0 !important;
  }
}
</style>
